import React, { useCallback, useEffect, memo, useRef, useMemo } from 'react';
import { View, Text, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  withSequence,
  withTiming,
  withRepeat,
  Extrapolation,
  interpolate,
  Easing,
  cancelAnimation,
} from 'react-native-reanimated';
import { useThemeColors, useThemeStore } from '../../../shared/theme';
import type { ThemeColors } from '../../../shared/theme/store';
import { SvgIcon } from '../../../components/common/SvgIcon';
import { ButtonLoader } from '../../../components/load-main/ButtonLoader';
import { useTranslation } from '../../../shared/i18n';
import { Clickable } from '../../../components/common/Clickable';
import type { MenuSaveBarStatus } from '../../components/MenuFlowBottomMacroBar';

const V2_BAR_PAD_TOP = 9;
const V2_CHIP_MIN_HEIGHT = 32;
const V2_BAR_PAD_ABOVE_HOME = 13;
const V2_LIST_EXTRA_CLEARANCE = 14;
const V2_TAB_BAR_CLEARANCE = 52;

/** FlatList bottom padding when the set2 save strip is visible. */
export function getSet2MenuListBottomContentPadding(safe_area_bottom: number) {
  return V2_BAR_PAD_TOP + V2_CHIP_MIN_HEIGHT + V2_BAR_PAD_ABOVE_HOME + safe_area_bottom + V2_LIST_EXTRA_CLEARANCE + V2_TAB_BAR_CLEARANCE;
}

function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T | undefined>(undefined);
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

type StatusColors = { bg: string; text: string; border: string; icon: string; breathe: boolean };

function getStatusColors(status: MenuSaveBarStatus, c: ThemeColors): StatusColors {
  const on_p = c.onPrimary;
  switch (status) {
    case 'synced':
      return { bg: c.borderLight, text: c.textSecondary, border: c.border, icon: c.textSecondary, breathe: false };
    case 'dirty':
      return { bg: c.primary, text: on_p, border: c.primary, icon: on_p, breathe: true };
    case 'saving':
      return { bg: c.primary, text: on_p, border: c.primary, icon: on_p, breathe: true };
    case 'saved':
      return { bg: c.success, text: c.onSuccess, border: c.success, icon: c.onSuccess, breathe: false };
    case 'error':
      return { bg: c.danger, text: c.onError, border: c.danger, icon: c.onError, breathe: true };
    default:
      return { bg: c.primary, text: on_p, border: c.primary, icon: on_p, breathe: false };
  }
}

const V2SaveActionButton = memo(
  ({ status, disabled, onPress }: { status: MenuSaveBarStatus; disabled: boolean; onPress: () => void }) => {
    const { t } = useTranslation();
    const theme_c = useThemeColors() ?? useThemeStore.getState().colors;
    const col = getStatusColors(status, theme_c);
    const prev_status = usePrevious(status);

    const label =
      status === 'dirty'
        ? t('menu.saveUnsaved')
        : status === 'saving'
          ? t('menu.saveSaving')
          : status === 'saved'
            ? t('menu.saveSaved')
            : status === 'error'
              ? t('menu.saveRetry')
              : t('menu.saveSynced');

    const press_scale = useSharedValue(1);
    const breathe = useSharedValue(0);
    const saved_bump = useSharedValue(0);
    const breathe_on = useSharedValue(0);
    const err_wiggle = useSharedValue(0);
    const saved_icon_pop = useSharedValue(0);

    useEffect(() => {
      const on = status === 'dirty' || status === 'saving' || status === 'error' ? 1 : 0;
      breathe_on.value = on;
    }, [status, breathe_on]);

    useEffect(() => {
      const on = status === 'dirty' || status === 'saving' || status === 'error';
      if (on) {
        breathe.value = withRepeat(
          withSequence(
            withTiming(1, { duration: 750, easing: Easing.inOut(Easing.ease) }),
            withTiming(0, { duration: 750, easing: Easing.inOut(Easing.ease) }),
          ),
          -1,
          true,
        );
        return () => {
          cancelAnimation(breathe);
          breathe.value = 0;
        };
      }
      cancelAnimation(breathe);
      breathe.value = 0;
    }, [status, breathe]);

    useEffect(() => {
      if (status === 'saved' && prev_status != null && prev_status !== 'saved') {
        saved_bump.value = 0;
        saved_bump.value = withSequence(
          withTiming(1, { duration: 140, easing: Easing.out(Easing.cubic) }),
          withTiming(0, { duration: 1000, easing: Easing.inOut(Easing.ease) }),
        );
        saved_icon_pop.value = 0.88;
        saved_icon_pop.value = withSequence(
          withSpring(1.2, { damping: 9, stiffness: 360 }),
          withTiming(1, { duration: 220, easing: Easing.out(Easing.cubic) }),
        );
      }
    }, [status, prev_status, saved_bump, saved_icon_pop]);

    useEffect(() => {
      if (status === 'error' && (prev_status === 'saving' || prev_status === 'dirty' || prev_status === 'synced')) {
        err_wiggle.value = 0;
        err_wiggle.value = withSequence(
          withTiming(14, { duration: 70, easing: Easing.inOut(Easing.ease) }),
          withTiming(-10, { duration: 55, easing: Easing.inOut(Easing.ease) }),
          withTiming(6, { duration: 55, easing: Easing.inOut(Easing.ease) }),
          withTiming(0, { duration: 130, easing: Easing.out(Easing.cubic) }),
        );
        return;
      }
      if (status !== 'error') {
        err_wiggle.value = 0;
      }
    }, [status, prev_status, err_wiggle]);

    useEffect(() => {
      if (status !== 'saved') {
        saved_icon_pop.value = 1;
      }
    }, [status, saved_icon_pop]);

    const wrap_style = useAnimatedStyle(() => {
      const br = 1 + 0.02 * breathe.value * breathe_on.value + 0.08 * saved_bump.value;
      return { transform: [{ scale: br * press_scale.value }] };
    });

    const error_icon_style = useAnimatedStyle(() => ({
      transform: [{ rotate: `${err_wiggle.value}deg` }],
    }));

    const saved_icon_style = useAnimatedStyle(() => ({
      transform: [{ scale: saved_icon_pop.value }],
    }));

    const on_press = useCallback(() => {
      if (disabled) {
        return;
      }
      press_scale.value = withSequence(
        withTiming(0.94, { duration: 85, easing: Easing.out(Easing.cubic) }),
        withSpring(1, { damping: 11, stiffness: 280 }),
      );
      onPress();
    }, [disabled, onPress, press_scale]);

    return (
      <View style={v2Styles.chipRow}>
        <Animated.View style={[v2Styles.chipWrap, { borderColor: col.border }, wrap_style]}>
          <Clickable
            onPress={on_press}
            disabled={disabled}
            contentStyle={v2Styles.chipContent}
            style={[v2Styles.chip, { backgroundColor: col.bg, borderColor: col.border }, disabled && v2Styles.chipDisabled]}
            accessibilityLabel={label}
            accessibilityRole="button"
            accessibilityState={{ disabled }}>
            {status === 'saving' ? (
              <ActivityIndicator size="small" color={col.text} style={v2Styles.chipIcon} />
            ) : status === 'synced' ? (
              <SvgIcon name="ellipse-outline" size={18} color={col.icon} style={v2Styles.chipIcon} />
            ) : status === 'saved' ? (
              <Animated.View style={saved_icon_style}>
                <SvgIcon name="checks" size={20} color={col.icon} style={v2Styles.chipIcon} />
              </Animated.View>
            ) : status === 'error' ? (
              <Animated.View style={error_icon_style}>
                <SvgIcon name="refresh" size={16} color={col.icon} style={v2Styles.chipIcon} />
              </Animated.View>
            ) : (
              <SvgIcon name="plus" size={16} color={col.icon} style={v2Styles.chipIcon} />
            )}
            <Text style={[v2Styles.chipText, { color: col.text }]} numberOfLines={1}>
              {status === 'saving' ? t('menu.saveSaving') : label}
            </Text>
          </Clickable>
        </Animated.View>
      </View>
    );
  },
);

V2SaveActionButton.displayName = 'V2SaveActionButton';

function createV2BarStyles(c: ThemeColors, appearance: 'light' | 'dark') {
  return StyleSheet.create({
    v2bar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      paddingHorizontal: 12,
    },
    v2barBgSolid: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: c.surface,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
    },
    v2barBgTertiary: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: appearance === 'dark' ? 'rgba(28,28,30,0.94)' : 'rgba(255,255,255,0.94)',
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: c.border,
      borderTopLeftRadius: 24,
      borderTopRightRadius: 24,
    },
    savingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      paddingVertical: 8,
    },
    savingText: {
      fontSize: 12,
      color: c.textSecondary,
    },
  });
}

interface MenuFlowBottomMacroBarSet2Props {
  macroTotals: {
    calories: number;
    protein: number;
    carbs: number;
    fats: number;
  };
  isComplete: boolean;
  isVisible: boolean;
  useSolidBackground?: boolean;
  isSyncing?: boolean;
  onPressSave?: () => void;
  saveStatus?: MenuSaveBarStatus;
  isSaveLoading?: boolean;
  saveButtonDisabled?: boolean;
  /** Ignored — set2 chrome never renders macro totals. */
  showMacroTotals?: boolean;
}

export const MenuFlowBottomMacroBarSet2 = memo(
  ({
    isVisible,
    useSolidBackground = false,
    isSyncing = false,
    onPressSave,
    saveButtonDisabled = false,
    isSaveLoading = false,
    saveStatus = 'synced',
  }: MenuFlowBottomMacroBarSet2Props) => {
    const { t } = useTranslation();
    const insets = useSafeAreaInsets();
    const theme_c = useThemeColors() ?? useThemeStore.getState().colors;
    const appearance = useThemeStore(s => s.appearance);
    const v2BarStyles = useMemo(() => createV2BarStyles(theme_c, appearance), [theme_c, appearance]);

    const effective_saving = saveStatus === 'saving' || isSaveLoading;
    const v2_status: MenuSaveBarStatus = saveStatus;

    const transition = useSharedValue(0);

    useEffect(() => {
      transition.value = withSpring(isVisible ? 1 : 0, { damping: 20, stiffness: 100 });
    }, [isVisible, transition]);

    const card_entrance = useAnimatedStyle(() => ({
      opacity: transition.value,
      transform: [{ translateY: interpolate(transition.value, [0, 1], [50, 0], Extrapolation.CLAMP) }],
    }));

    if (!isVisible) {
      return null;
    }

    const show_save_chip = Boolean(onPressSave);
    const show_syncing_only = isSyncing && !onPressSave;

    if (!show_save_chip && !show_syncing_only) {
      return null;
    }

    return (
      <Animated.View
        style={[
          v2BarStyles.v2bar,
          card_entrance,
          { zIndex: 100, elevation: 100 },
          {
            paddingTop: V2_BAR_PAD_TOP,
            paddingBottom: V2_BAR_PAD_ABOVE_HOME + insets.bottom + V2_TAB_BAR_CLEARANCE,
          },
        ]}>
        {useSolidBackground ? (
          <View style={v2BarStyles.v2barBgSolid} pointerEvents="none" />
        ) : (
          <View style={v2BarStyles.v2barBgTertiary} pointerEvents="none" />
        )}
        {show_syncing_only ? (
          <View style={v2BarStyles.savingRow}>
            <ButtonLoader size={18} color={theme_c.primary} />
            <Text style={v2BarStyles.savingText}>{t('account.saving')}</Text>
          </View>
        ) : (
          <V2SaveActionButton status={v2_status} disabled={saveButtonDisabled || effective_saving} onPress={onPressSave!} />
        )}
      </Animated.View>
    );
  },
);

MenuFlowBottomMacroBarSet2.displayName = 'MenuFlowBottomMacroBarSet2';

const v2Styles = StyleSheet.create({
  chipRow: { width: '100%', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  chipContent: {
    width: '100%',
    minHeight: V2_CHIP_MIN_HEIGHT,
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
  },
  chipWrap: { width: '100%', maxWidth: 360, alignSelf: 'center', borderWidth: 1, borderRadius: 10 },
  chip: {
    minHeight: V2_CHIP_MIN_HEIGHT,
    width: '100%',
    paddingVertical: 4,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'stretch',
  },
  chipDisabled: { opacity: 0.55 },
  chipIcon: { alignSelf: 'center' },
  chipText: {
    fontSize: 13,
    fontWeight: '600',
    flexShrink: 1,
    lineHeight: 18,
    ...Platform.select({ android: { includeFontPadding: false } }),
  },
});
